import React, { useEffect, useMemo, useState } from 'react';
import { CreditCard, RefreshCw, Plus, Save, Wallet, SlidersHorizontal, Pencil, ArrowRightLeft } from 'lucide-react';

import { Button, Card, Input, Modal, Badge } from '../../../components/ui';
import { creditoService } from '../../../services/creditoService';
import { Cliente, CreditoBalanceDiario, CreditoConfigLocal, CreditoMovimientoGlobal, CreditoResumenGlobal } from '../../../types/clientes.types';
import { formatBackendDateTimeCR, fechaCRYMD } from '../../../utils/date';

const hoyISO = () => fechaCRYMD();
const colones = (value: number | string | null | undefined) => `₡${Math.round(Number(value || 0)).toLocaleString('es-CR')}`;

interface ClienteForm {
  nombre: string;
  telefono: string;
  local_id: string;
  limite_credito: string;
  usuario_login: string;
  password: string;
}

export const CreditosPage: React.FC = () => {
  const [resumen, setResumen] = useState<CreditoResumenGlobal | null>(null);
  const [balances, setBalances] = useState<CreditoBalanceDiario[]>([]);
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [movimientos, setMovimientos] = useState<CreditoMovimientoGlobal[]>([]);
  const [configs, setConfigs] = useState<CreditoConfigLocal[]>([]);
  const [fechaDesde, setFechaDesde] = useState(hoyISO());
  const [fechaHasta, setFechaHasta] = useState(hoyISO());
  const [localFiltro, setLocalFiltro] = useState('');
  const [clienteFiltro, setClienteFiltro] = useState('');
  const [tipoFiltro, setTipoFiltro] = useState('');
  const [estadoFiltro, setEstadoFiltro] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [clienteModal, setClienteModal] = useState(false);
  const [editarModal, setEditarModal] = useState(false);
  const [clienteEditando, setClienteEditando] = useState<Cliente | null>(null);
  const [editForm, setEditForm] = useState({ nombre: '', telefono: '', limite_credito: '', usuario_login: '', password: '' });
  const [accionCliente, setAccionCliente] = useState<Cliente | null>(null);
  const [accionTipo, setAccionTipo] = useState<'ABONO' | 'AJUSTE' | null>(null);
  const [montoAccion, setMontoAccion] = useState('');
  const [motivoAccion, setMotivoAccion] = useState('');
  const [ajusteTipo, setAjusteTipo] = useState<'POSITIVO' | 'NEGATIVO'>('POSITIVO');
  const [form, setForm] = useState<ClienteForm>({ nombre: '', telefono: '', local_id: '', limite_credito: '', usuario_login: '', password: '' });
  const [transferCliente, setTransferCliente] = useState<Cliente | null>(null);
  const [transferDestino, setTransferDestino] = useState('');
  const [transferMotivo, setTransferMotivo] = useState('');
  const [transferLoading, setTransferLoading] = useState(false);

  const cargar = async () => {
    setLoading(true);
    setError('');
    try {
      const params: Record<string, string> = {};
      if (fechaDesde) params.fecha_desde = fechaDesde;
      if (fechaHasta) params.fecha_hasta = fechaHasta;
      if (localFiltro) params.local_id = localFiltro;
      if (clienteFiltro) params.cliente_id = clienteFiltro;
      if (tipoFiltro) params.tipo = tipoFiltro;
      if (estadoFiltro) params.estado = estadoFiltro;
      const [resumenData, configData, clientesData, balanceData, movimientosData] = await Promise.all([
        creditoService.obtenerResumenGlobal(),
        creditoService.listarConfiguracion(),
        creditoService.listarClientesBanca({ local_id: localFiltro || undefined, estado: estadoFiltro || undefined }),
        creditoService.obtenerBalanceDiario(fechaDesde || hoyISO(), localFiltro || undefined),
        creditoService.listarMovimientos(params),
      ]);
      setResumen(resumenData);
      setConfigs(configData);
      setClientes(clientesData);
      setBalances(balanceData);
      setMovimientos(movimientosData);
    } catch (err: any) {
      setError(err.message || 'No se pudo cargar créditos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { cargar(); }, []);

  const locales = useMemo(() => configs.map((config) => ({ id: config.local_id, nombre: config.local_nombre })), [configs]);

  const guardarCliente = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!form.nombre.trim() || !form.local_id) {
      setError('Nombre y local son obligatorios');
      return;
    }
    try {
      await creditoService.crearClienteBanca({
        nombre: form.nombre.trim(),
        telefono: form.telefono.trim() || undefined,
        local_id: form.local_id,
        limite_credito: Number(form.limite_credito || 0),
        usuario_login: form.usuario_login.trim() || undefined,
        password: form.password || undefined,
      });
      setClienteModal(false);
      setForm({ nombre: '', telefono: '', local_id: '', limite_credito: '', usuario_login: '', password: '' });
      await cargar();
    } catch (err: any) {
      setError(err.message || 'No se pudo crear cliente');
    }
  };

  const abrirEditar = (cliente: Cliente) => {
    setClienteEditando(cliente);
    setEditForm({
      nombre: cliente.nombre,
      telefono: cliente.telefono || '',
      limite_credito: String(cliente.limite_credito ?? 0),
      usuario_login: cliente.usuario_login || '',
      password: '',
    });
    setEditarModal(true);
  };

  const actualizarCliente = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!clienteEditando) return;
    if (!editForm.nombre.trim()) {
      setError('El nombre es obligatorio');
      return;
    }
    try {
      await creditoService.actualizarClienteBanca(clienteEditando.id, {
        nombre: editForm.nombre.trim(),
        telefono: editForm.telefono.trim() || undefined,
        limite_credito: Number(editForm.limite_credito || 0),
        usuario_login: editForm.usuario_login.trim() || undefined,
        password: editForm.password || undefined,
      });
      setEditarModal(false);
      setClienteEditando(null);
      await cargar();
    } catch (err: any) {
      setError(err.message || 'No se pudo actualizar el cliente');
    }
  };

  const guardarConfig = async (config: CreditoConfigLocal) => {
    try {
      await creditoService.guardarConfigLocal(config.local_id, {
        ...config,
        estado: config.puede_vender_credito ? 'ACTIVO' : config.estado,
      });
      await cargar();
    } catch (err: any) {
      setError(err.message || 'No se pudo guardar configuración');
    }
  };

  const eliminarCliente = async (cliente: Cliente) => {
    if (!window.confirm(`¿Eliminar a "${cliente.nombre}"? Esta acción no se puede deshacer.`)) return;
    try {
      await creditoService.eliminarCliente(cliente.id);
      await cargar();
    } catch (err: any) {
      setError(err.message || 'No se pudo eliminar el cliente');
    }
  };

  const abrirTransferencia = (cliente: Cliente) => {
    setTransferCliente(cliente);
    setTransferDestino('');
    setTransferMotivo('');
    setError('');
  };

  const ejecutarTransferencia = async () => {
    if (!transferCliente) return;
    if (!transferDestino) {
      setError('Seleccione el vendedor destino');
      return;
    }
    if (transferDestino === transferCliente.local_id) {
      setError('El cliente ya pertenece a ese vendedor');
      return;
    }
    const destinoNombre = locales.find((l) => l.id === transferDestino)?.nombre || transferDestino;
    if (!window.confirm(`¿Transferir a "${transferCliente.nombre}" al vendedor "${destinoNombre}"? Sus créditos abiertos se reasignarán.`)) return;
    setTransferLoading(true);
    try {
      await creditoService.transferirCredito(transferCliente.id, {
        vendedor_destino_id: transferDestino,
        motivo: transferMotivo.trim() || undefined,
      });
      setTransferCliente(null);
      setTransferDestino('');
      setTransferMotivo('');
      await cargar();
    } catch (err: any) {
      setError(err.message || 'No se pudo transferir el crédito');
    } finally {
      setTransferLoading(false);
    }
  };

  const ejecutarAccionCliente = async () => {
    if (!accionCliente || !accionTipo) return;
    const monto = Number(montoAccion);
    if (!Number.isFinite(monto) || monto <= 0) {
      setError('Monto inválido');
      return;
    }
    if (accionTipo === 'AJUSTE' && !motivoAccion.trim()) {
      setError('El motivo es obligatorio para ajustes');
      return;
    }
    try {
      if (accionTipo === 'ABONO') {
        await creditoService.abonarCliente(accionCliente.id, { monto, descripcion: motivoAccion || 'Abono registrado por banca' });
      } else {
        await creditoService.ajustarCliente(accionCliente.id, { monto, tipo: ajusteTipo, motivo: motivoAccion.trim() });
      }
      setAccionCliente(null);
      setAccionTipo(null);
      setMontoAccion('');
      setMotivoAccion('');
      await cargar();
    } catch (err: any) {
      setError(err.message || 'No se pudo registrar la operación');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Créditos</h1>
          <p className="text-gray-500">Control de clientes, permisos, movimientos y balance diario.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" leftIcon={<RefreshCw size={16} className={loading ? 'animate-spin' : ''} />} onClick={cargar}>Refrescar</Button>
          <Button variant="primary" leftIcon={<Plus size={16} />} onClick={() => setClienteModal(true)}>Cliente</Button>
        </div>
      </div>

      {error && <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <Card><p className="text-xs text-gray-500">Saldo pendiente</p><p className="text-xl font-bold">{colones(resumen?.saldo_pendiente_total)}</p></Card>
        <Card><p className="text-xs text-gray-500">Crédito hoy</p><p className="text-xl font-bold text-amber-600">{colones(resumen?.creditos_dados_hoy)}</p></Card>
        <Card><p className="text-xs text-gray-500">Abonos hoy</p><p className="text-xl font-bold text-green-600">{colones(resumen?.abonos_recibidos_hoy)}</p></Card>
        <Card><p className="text-xs text-gray-500">Balance caja crédito</p><p className="text-xl font-bold">{colones(resumen?.balance_caja_credito_hoy)}</p></Card>
        <Card><p className="text-xs text-gray-500">Clientes activos</p><p className="text-xl font-bold">{resumen?.clientes_activos || 0}</p></Card>
      </div>

      <Card>
        <div className="flex items-center gap-2 mb-4"><SlidersHorizontal size={18} /><h2 className="font-semibold">Filtros</h2></div>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          <Input label="Desde" type="date" value={fechaDesde} onChange={(e) => setFechaDesde(e.target.value)} />
          <Input label="Hasta" type="date" value={fechaHasta} onChange={(e) => setFechaHasta(e.target.value)} />
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Local</label><select className="w-full border rounded-lg px-3 py-2" value={localFiltro} onChange={(e) => setLocalFiltro(e.target.value)}><option value="">Todos</option>{locales.map((local) => <option key={local.id} value={local.id}>{local.nombre}</option>)}</select></div>
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Tipo</label><select className="w-full border rounded-lg px-3 py-2" value={tipoFiltro} onChange={(e) => setTipoFiltro(e.target.value)}><option value="">Todos</option><option value="CREDITO_OTORGADO">Crédito</option><option value="ABONO_RECIBIDO">Abono</option><option value="AJUSTE_POSITIVO">Ajuste +</option><option value="AJUSTE_NEGATIVO">Ajuste -</option></select></div>
          <div className="flex items-end"><Button className="w-full" variant="secondary" onClick={cargar}>Aplicar</Button></div>
        </div>
      </Card>

      <Card>
        <div className="flex items-center gap-2 mb-4"><Wallet size={18} /><h2 className="font-semibold">Balance diario por local</h2></div>
        <div className="overflow-x-auto"><table className="min-w-full text-sm"><thead><tr className="text-left text-gray-500 border-b"><th className="py-2">Local</th><th>Saldo anterior</th><th>Créditos</th><th>Abonos</th><th>Ajustes</th><th>Saldo final</th><th>Balance caja</th></tr></thead><tbody>{balances.map((row) => <tr key={row.local_id} className="border-b"><td className="py-2 font-medium">{row.local_nombre}</td><td>{colones(row.saldo_anterior)}</td><td>{colones(row.creditos_otorgados)}</td><td>{colones(row.abonos_recibidos)}</td><td>{colones(row.ajustes_positivos - row.ajustes_negativos)}</td><td>{colones(row.saldo_final)}</td><td className={row.balance_caja_credito >= 0 ? 'text-green-700 font-semibold' : 'text-red-700 font-semibold'}>{colones(row.balance_caja_credito)}</td></tr>)}</tbody></table></div>
      </Card>

      <Card>
        <div className="flex items-center gap-2 mb-4"><CreditCard size={18} /><h2 className="font-semibold">Clientes de crédito</h2></div>
        <div className="overflow-x-auto"><table className="min-w-full text-sm"><thead><tr className="text-left text-gray-500 border-b"><th className="py-2">Cliente</th><th>Local</th><th>Límite</th><th>Saldo</th><th>Login</th><th>Estado</th><th></th></tr></thead><tbody>{clientes.map((cliente) => <tr key={cliente.id} className="border-b"><td className="py-2"><p className="font-medium">{cliente.nombre}</p><p className="text-xs text-gray-500">{cliente.telefono || '-'}</p></td><td>{locales.find((local) => local.id === cliente.local_id)?.nombre || cliente.local_id}</td><td>{colones(cliente.limite_credito)}</td><td className="font-semibold">{colones(cliente.saldo_pendiente)}</td><td>{cliente.usuario_login ? <span className="font-mono text-xs text-green-700 bg-green-50 px-1 rounded">{cliente.usuario_login}</span> : <span className="text-xs text-gray-400">sin login</span>}</td><td><Badge variant={cliente.activo ? 'success' : 'neutral'}>{cliente.activo ? 'ACTIVO' : 'INACTIVO'}</Badge></td><td><div className="flex gap-2"><Button size="sm" variant="secondary" leftIcon={<Pencil size={13} />} onClick={() => abrirEditar(cliente)}>Editar</Button><Button size="sm" variant="secondary" onClick={() => { setAccionCliente(cliente); setAccionTipo('ABONO'); }}>Abono</Button><Button size="sm" variant="secondary" onClick={() => { setAccionCliente(cliente); setAccionTipo('AJUSTE'); }}>Ajuste</Button><Button size="sm" variant="secondary" leftIcon={<ArrowRightLeft size={13} />} onClick={() => abrirTransferencia(cliente)}>Transferir</Button>{Number(cliente.saldo_pendiente) === 0 && <Button size="sm" variant="danger" onClick={() => eliminarCliente(cliente)}>Eliminar</Button>}</div></td></tr>)}</tbody></table></div>
      </Card>

      <Card>
        <div className="flex items-center gap-2 mb-4"><Save size={18} /><h2 className="font-semibold">Configuración por local</h2></div>
        <div className="space-y-2">
          {configs.map((config) => {
            const daCred = config.puede_vender_credito;
            const update = (patch: Partial<typeof config>) =>
              setConfigs((prev) => prev.map((item) => item.local_id === config.local_id ? { ...item, ...patch } : item));
            return (
              <div key={config.local_id} className="flex flex-wrap items-center gap-3 border rounded-lg px-4 py-3">
                {/* Nombre */}
                <p className="font-medium w-24 shrink-0">{config.local_nombre}</p>

                {/* Toggle principal */}
                <label className="flex items-center gap-2 text-sm font-semibold cursor-pointer select-none">
                  <input
                    type="checkbox"
                    className="w-4 h-4 accent-blue-600"
                    checked={daCred}
                    onChange={(e) => update({ puede_vender_credito: e.target.checked, puede_registrar_abonos: e.target.checked ? config.puede_registrar_abonos : false })}
                  />
                  Da crédito
                </label>

                {/* Límite — solo si da crédito */}
                <div className="flex items-center gap-1">
                  <span className="text-xs text-gray-500 whitespace-nowrap">Límite ₡</span>
                  <input
                    type="number"
                    min={0}
                    className={`border rounded px-2 py-1 text-sm w-28 ${!daCred ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : ''}`}
                    disabled={!daCred}
                    value={config.limite_credito_total}
                    onChange={(e) => update({ limite_credito_total: Number(e.target.value) })}
                    placeholder="0"
                  />
                </div>

                {/* Abonos — solo disponible si da crédito */}
                <label className={`flex items-center gap-2 text-sm cursor-pointer select-none ${!daCred ? 'opacity-40 cursor-not-allowed' : ''}`}>
                  <input
                    type="checkbox"
                    className="w-4 h-4 accent-green-600"
                    disabled={!daCred}
                    checked={config.puede_registrar_abonos}
                    onChange={(e) => update({ puede_registrar_abonos: e.target.checked })}
                  />
                  Abonos
                </label>

                <Button size="sm" variant="primary" onClick={() => guardarConfig(config)}>Guardar</Button>

                {/* Indicador visual */}
                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${daCred ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                  {daCred ? 'ACTIVO' : 'INACTIVO'}
                </span>
              </div>
            );
          })}
        </div>
      </Card>

      <Card>
        <h2 className="font-semibold mb-4">Historial de movimientos</h2>
        <div className="overflow-x-auto"><table className="min-w-full text-sm"><thead><tr className="text-left text-gray-500 border-b"><th className="py-2">Fecha</th><th>Tipo</th><th>Local</th><th>Monto</th><th>Saldo anterior</th><th>Saldo nuevo</th><th>Motivo</th></tr></thead><tbody>{movimientos.map((mov) => <tr key={mov.id} className="border-b"><td className="py-2">{formatBackendDateTimeCR(mov.fecha_hora)}</td><td>{mov.tipo_movimiento}</td><td>{locales.find((local) => local.id === mov.local_id)?.nombre || mov.local_id}</td><td>{colones(mov.monto)}</td><td>{colones(mov.saldo_anterior)}</td><td>{colones(mov.saldo_nuevo)}</td><td>{mov.motivo || mov.descripcion || '-'}</td></tr>)}</tbody></table></div>
      </Card>

      <Modal isOpen={clienteModal} onClose={() => setClienteModal(false)} title="Crear cliente de crédito" size="md">
        <form onSubmit={guardarCliente} className="space-y-3"><Input label="Nombre" value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} /><Input label="Teléfono" value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })} /><div><label className="block text-sm font-medium text-gray-700 mb-1">Local</label><select className="w-full border rounded-lg px-3 py-2" value={form.local_id} onChange={(e) => setForm({ ...form, local_id: e.target.value })}><option value="">Seleccione local</option>{locales.map((local) => <option key={local.id} value={local.id}>{local.nombre}</option>)}</select></div><Input label="Límite" type="number" value={form.limite_credito} onChange={(e) => setForm({ ...form, limite_credito: e.target.value })} /><Input label="Usuario/login opcional" value={form.usuario_login} onChange={(e) => setForm({ ...form, usuario_login: e.target.value })} /><Input label="Contraseña opcional" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /><div className="flex gap-2 pt-2"><Button type="button" variant="secondary" onClick={() => setClienteModal(false)} className="flex-1">Cancelar</Button><Button type="submit" variant="primary" className="flex-1">Crear</Button></div></form>
      </Modal>

      <Modal isOpen={!!accionCliente && !!accionTipo} onClose={() => { setAccionCliente(null); setAccionTipo(null); }} title={accionTipo === 'ABONO' ? 'Registrar abono' : 'Registrar ajuste'} size="sm">
        <div className="space-y-3"><p className="font-medium">{accionCliente?.nombre}</p>{accionTipo === 'AJUSTE' && <select className="w-full border rounded-lg px-3 py-2" value={ajusteTipo} onChange={(e) => setAjusteTipo(e.target.value as 'POSITIVO' | 'NEGATIVO')}><option value="POSITIVO">Ajuste positivo</option><option value="NEGATIVO">Ajuste negativo</option></select>}<Input label="Monto" type="number" value={montoAccion} onChange={(e) => setMontoAccion(e.target.value)} /><Input label={accionTipo === 'AJUSTE' ? 'Motivo obligatorio' : 'Descripción'} value={motivoAccion} onChange={(e) => setMotivoAccion(e.target.value)} /><div className="flex gap-2 pt-2"><Button variant="secondary" onClick={() => { setAccionCliente(null); setAccionTipo(null); }} className="flex-1">Cancelar</Button><Button variant="primary" onClick={ejecutarAccionCliente} className="flex-1">Guardar</Button></div></div>
      </Modal>

      <Modal isOpen={!!transferCliente} onClose={() => setTransferCliente(null)} title="Transferir crédito a otro vendedor" size="sm">
        <div className="space-y-3">
          <p className="font-medium">{transferCliente?.nombre}</p>
          <p className="text-xs text-gray-500">
            Vendedor actual: {locales.find((l) => l.id === transferCliente?.local_id)?.nombre || transferCliente?.local_id || '-'}
            {' · '}Saldo: {colones(transferCliente?.saldo_pendiente)}
          </p>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Vendedor destino</label>
            <select className="w-full border rounded-lg px-3 py-2" value={transferDestino} onChange={(e) => setTransferDestino(e.target.value)}>
              <option value="">Seleccione vendedor</option>
              {locales.filter((l) => l.id !== transferCliente?.local_id).map((local) => <option key={local.id} value={local.id}>{local.nombre}</option>)}
            </select>
          </div>
          <Input label="Motivo (opcional)" value={transferMotivo} onChange={(e) => setTransferMotivo(e.target.value)} />
          <div className="flex gap-2 pt-2">
            <Button variant="secondary" onClick={() => setTransferCliente(null)} className="flex-1" disabled={transferLoading}>Cancelar</Button>
            <Button variant="primary" leftIcon={<ArrowRightLeft size={15} />} onClick={ejecutarTransferencia} className="flex-1" disabled={transferLoading}>{transferLoading ? 'Transfiriendo...' : 'Transferir'}</Button>
          </div>
        </div>
      </Modal>

      <Modal isOpen={editarModal} onClose={() => { setEditarModal(false); setClienteEditando(null); }} title="Editar cliente de crédito" size="md">
        <form onSubmit={actualizarCliente} className="space-y-3">
          <Input label="Nombre" value={editForm.nombre} onChange={(e) => setEditForm({ ...editForm, nombre: e.target.value })} />
          <Input label="Teléfono" value={editForm.telefono} onChange={(e) => setEditForm({ ...editForm, telefono: e.target.value })} />
          <Input label="Límite de crédito" type="number" value={editForm.limite_credito} onChange={(e) => setEditForm({ ...editForm, limite_credito: e.target.value })} />
          <div className="border-t pt-3">
            <p className="text-xs text-gray-500 mb-2 font-medium">Credenciales de acceso al portal</p>
            <div className="space-y-2">
              <Input label="Usuario / Login" value={editForm.usuario_login} onChange={(e) => setEditForm({ ...editForm, usuario_login: e.target.value })} placeholder="Dejar vacío para sin acceso" />
              <Input label="Nueva contraseña (dejar vacío para no cambiar)" type="password" value={editForm.password} onChange={(e) => setEditForm({ ...editForm, password: e.target.value })} placeholder="••••••••" />
            </div>
          </div>
          <div className="flex gap-2 pt-2">
            <Button type="button" variant="secondary" onClick={() => { setEditarModal(false); setClienteEditando(null); }} className="flex-1">Cancelar</Button>
            <Button type="submit" variant="primary" leftIcon={<Save size={15} />} className="flex-1">Guardar</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default CreditosPage;
